import React, { useRef, useState, useEffect } from 'react';
import { FaEllipsisH } from 'react-icons/fa';
import ActionsMenu from './ActionsMenu';
import CameraPlaceholder from '../live/CameraPlaceholder';
import ZoomableContainer from '../common/ZoomableContainer';

// A pointer that moves more than this between down and up is a drag, not a tap (matches dnd-kit's 8px
// activation distance) — so a cell drag-to-swap never also fires the single-tap cell SELECT (#3).
const TAP_MOVE_PX = 8;
// Material "warning" triangle — the media-error glyph (the clip won't load/decode).
const ERROR_ICON_PATH = 'M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z';
// Cell chrome that owns its own gestures — a double-tap on these must NOT trigger the single-view focus.
// The drag-handle is chrome too: it owns the swap-drag (its own dnd listeners), so the body's tap/zoom
// handlers must ignore it.
const CHROME_SEL = '.mcpb-cell__cam-name, .mcpb-actions, .mcpb-cell__chrome-tr, .mcpb-cell__drag-handle';

/**
 * Cell — one slot's video + chrome for MCPlayback. Reused at conf 1/2/4.
 *
 * Top-right cluster (#16, David 2026-06-23 — ported from the tablet mock, applied at every size): a
 * surfaced quick-action ROW (Snapshot · Download · Archive · Export) followed by the per-cell `•••` menu.
 * The row is REVEALED on hover (mouse inside the cell) or single-tap (touch → `actionsShown`); the cog
 * "Always-show quick actions" toggle (default ON) pins it visible without hover via the container's
 * `data-quick-actions="always"`. Archive + Export DOUBLE as status — they stay visible whenever the clip
 * is archived/exported (filled green/accent), so there's no separate status-icon row. The `•••` holds the
 * rest (View single, Bookmark v2) AND the four primaries — the ••• always lists the full set (David
 * 2026-08-06) so every action is reachable from the menu even when the row is pinned. A right-click on
 * the video opens the same ••• menu (parity with Live's tile context-menu). All chrome sits on the
 * OUTER `.mcpb-cell` (letterbox-aware), never on the video.
 *
 * Presentational: the parent resolves the slot's camera, its identity color, and the built stream
 * URL for the current clip, then passes them in. The parent also owns the playhead and drives this
 * cell's <video> through `videoRef` (Phase 1e useSlotVideo). In conf=1 the cell is also the
 * camera-picker entry point (Phase 1d): the empty "+ Add camera" prompt opens the drawer
 * (onAddCamera); a populated cell's lower-left cam-name re-opens it to replace (onChangeCamera). The
 * transport (1e) floats over the cell bottom via the `transportOverlay` slot; conf>1 cam-strip is Phase 2.
 *
 * Overlay set (David 2026-06-20, clarified 2026-06-21): the standard CCTV pair — cam name lower-left
 * (which also IS the change-camera button) + timestamp lower-right — burned onto every cell at every
 * conf and orientation. The timestamp (`infoText`) is this cell's OWN playback time (clip start + the
 * player's real position — SlotCell/useCellClock), not the shared master-playhead, so a cross-cam desync
 * shows honestly instead of being masked by one shared clock. It replaces the old top-left id-pill. Always on (no toggle): the name is
 * the cell's identity + change affordance, so it can't sit behind an optional switch. This is SEPARATE
 * from — not a duplicate of — the cog-gated "Camera Info overlay" (`streamInfo`, top-LEFT corner): the
 * on-video VIDEO/STREAM diagnostics (resolution · est. FPS · transcode profile · buffer state — the
 * desktop DstreamView "Toggle Info" equivalent), which is unrelated to the name/timestamp label.
 */
const Cell = ({
  camera, videoUrl = '', videoRef,
  mediaSurface = null, wcReady = false, livePreview = null,
  transportOverlay = null, infoText = '', streamInfo = null,
  gap = false, loading = false,
  buffering = false, bufferingLabel = '', loadError = null,
  recovering = false, failed = false,
  quickActions = [], onQuickAction, menuItems = [],
  zoomed = false, dimmed = false, canZoom = false, onZoom,
  isEview = false,
  camLabel = '',
  flash = false,
  slotIndex, dragging = false, dragTarget = false, selected = false, hovered = false, onSelect, actionsShown = false,
  dragEnabled = false,
  dndListeners = null, dndAttributes = null, setDndRef = null,
  onAddCamera, onChangeCamera,
  onMediaError,
}) => {
  // Double-tap / double-click anywhere on the cell body (not its chrome) toggles single-view focus at
  // conf>1 (canZoom). One pointer-based detector covers touch AND mouse; single taps do nothing. conf=1 = no-op.
  // Declared before the empty-cell early return so the hook is called unconditionally (rules-of-hooks).
  // Unified double-click/tap → single-view, matching Live (VideoViewWs onDoubleClick + dblClickGuard).
  // The ZoomableContainer forwards the double-click here — ONE mechanism, replacing the old separate
  // pointerup `lastTap` double-tap that ran alongside the digital zoom and collided with it. The guard
  // dedupes the nested inner+wrapper double-fire (one gesture = one action, mouse + touch); a double-
  // click while DIGITALLY zoomed clears the zoom first (parity with Live's "double-click restores grid").
  const zoomApiRef = useRef(null);
  const tapStart = useRef({ x: 0, y: 0 });
  const handleCellDoubleClick = () => {
    // Double-click while DIGITALLY zoomed clears the zoom first (parity with Live); otherwise toggle
    // single-view. Only the ZoomableContainer forwards this (a single handler — the inner drag-handle
    // stops its own double-click), so there's no nested double-fire to dedupe.
    if (zoomApiRef.current && zoomApiRef.current.isZoomed) { zoomApiRef.current.resetZoom(); return; }
    if (canZoom && onZoom) onZoom();
  };

  // Media-error overlay: the clip loaded into <video> failed to decode/load. The retry/backoff now lives
  // in useSlotVideo (SlotCell), which drives the `recovering` (a reload is in flight → show the spinner,
  // NOT an error) and `failed` (backoff exhausted → show "Can't play this clip") props — Cell just renders
  // them, so there's no cross-hook error flash. Cell's own <video> onError only fires the session-death
  // probe (onMediaError → the View re-fetches the clip URL; a 500 = expired session, per #86bb7phhh).
  // First-frame (David): while a clip has a src but hasn't decoded its first frame, show a spinner instead
  // of a bare poster/black tile. Reset when the src changes (each clip loads fresh).
  const [firstFrame, setFirstFrame] = useState(false);
  useEffect(() => { setFirstFrame(false); }, [videoUrl]);
  // Right-click → open the ••• menu (parity with Live's tile context-menu, David 2026-08-06). A
  // monotonically-bumped signal ActionsMenu watches; routed through ZoomableContainer, which forwards
  // contextmenu ONLY when NOT zoomed (a zoomed right-click resets the zoom instead).
  const [menuSignal, setMenuSignal] = useState(0);
  const loadingFrame = !!videoUrl && !firstFrame;
  // Frozen last frame (task 3, David 2026-08-05): capture the live frame to a hidden canvas while playing,
  // so when this cam drops into a GAP we freeze that last decoded frame (greyed) instead of the DIVIDIA
  // placeholder — a "this is where it left off; no footage here right now" cue. hasLastFrame resets per
  // cam so a swap never shows the previous cam's frame.
  const lastFrameRef = useRef(null);
  const [hasLastFrame, setHasLastFrame] = useState(false);
  // Reset the element + frozen frame when the CAM in this slot changes (swap/replace) so the cell shows
  // the placeholder poster instead of the PREVIOUS cam's last frame until the new clip decodes
  // (#9 stale-frame-on-swap). Keyed on cam identity so a normal clip-advance within one cam doesn't flash.
  const camKey = camera ? `${camera.bSerial}_${camera.bCamera}` : null;
  useEffect(() => {
    const v = videoRef && videoRef.current;
    if (v) { try { v.load(); } catch (e) { /* jsdom/no media pipeline */ } }
    setHasLastFrame(false);
  }, [camKey, videoRef]);
  // Grab the current frame while actively showing video (native <video> path only), so a subsequent gap
  // can paint the frozen last frame. A light 1s poll (the frozen frame needn't be sub-second fresh) plus
  // an immediate grab when playback starts. drawImage can throw on a cross-origin (eview) frame — the grey
  // canvas still cues the gap, so hasLastFrame is marked on any decoded frame regardless of that.
  useEffect(() => {
    if (gap || !firstFrame || mediaSurface) return undefined;
    const grab = () => {
      const vv = videoRef && videoRef.current;
      const canvas = lastFrameRef.current;
      if (!vv || !canvas || !vv.videoWidth || vv.readyState < 2) return;
      canvas.width = vv.videoWidth; canvas.height = vv.videoHeight;
      try { const ctx = canvas.getContext('2d'); if (ctx) ctx.drawImage(vv, 0, 0, canvas.width, canvas.height); }
      catch (e) { /* tainted/unsupported — the grey canvas still cues the gap */ }
      setHasLastFrame(true);
    };
    grab();
    const timer = setInterval(grab, 1000);
    return () => clearInterval(timer);
  }, [gap, firstFrame, mediaSurface, videoRef]);

  // Empty cell = a truly-null slot OR a placeholder the view defines as empty (fEnable:false). Both render
  // identically: the DIVIDIA placeholder card BEHIND a "+ Add camera" prompt — one empty look, addable (David).
  if (!camera || camera.fEnable === false) {
    // An empty cell is a valid DROP target (drag a cam onto it via dnd-kit) but cannot be grabbed;
    // dragTarget (the View's droppable isOver) drives the dashed outline.
    return (
      <div
        ref={setDndRef}
        className={`mcpb-cell mcpb-cell--empty${dragTarget ? ' mcpb-cell--drag-target' : ''}`}
        data-mcpb-slot={slotIndex}
      >
        <CameraPlaceholder />
        <div className="mcpb-cell__empty-prompt">
          <button type="button" className="mcpb-cell__empty-prompt__bubble" aria-label="Add camera" onClick={onAddCamera}>
            <span className="mcpb-cell__empty-prompt__plus">+</span>
            <span>Add camera</span>
          </button>
        </div>
      </div>
    );
  }

  // Grip-handle vs body drag — mirrors Live's DraggableTile (VideoViewWs). On a FINE pointer (desktop
  // mouse) the swap-drag is initiated from a small hover-revealed grip HANDLE only, leaving the cell
  // BODY's click-drag free for ZoomableContainer's digital zoom (no gesture collision). On a COARSE
  // pointer (touch) there's NO handle — the body long-press (dnd TouchSensor, 250ms) starts the swap and
  // pinch/pan/tap can't be mistaken for it, so nothing obscures the video.
  const finePointer = typeof window !== 'undefined' && window.matchMedia
    ? window.matchMedia('(pointer: fine)').matches : true;
  const useHandle = dragEnabled && finePointer;

  // A clean tap (no drag movement, not on chrome) SELECTS this cell (#3 — the View then routes a
  // sidebar-cam tap INTO the focused cell). A second tap within the window ZOOMs instead (conf>1).
  const handleCellPointerUp = (e) => {
    if (e.target.closest && e.target.closest(CHROME_SEL)) return; // chrome owns its own taps
    const moved = Math.abs((e.clientX || 0) - tapStart.current.x) > TAP_MOVE_PX
      || Math.abs((e.clientY || 0) - tapStart.current.y) > TAP_MOVE_PX;
    if (moved) return; // that was a drag, not a tap
    // Single tap selects; double-click-to-single is handled by handleCellDoubleClick on the
    // ZoomableContainer (unified with Live) — no separate pointerup double-tap here anymore.
    if (onSelect) onSelect();
  };
  // Drag-to-rearrange (conf>1): record the press origin (the tap-vs-drag discriminator) then start the
  // drag hook. Grab the cell body (not its chrome) to swap slots.
  const handlePointerDown = (e) => {
    tapStart.current = { x: e.clientX || 0, y: e.clientY || 0 };
    if (e.target.closest && e.target.closest(CHROME_SEL)) return; // chrome owns its gestures — no drag
    // Fine pointer: the body does NOT start a swap-drag (the grip handle does) — so a body click-drag is
    // free for the digital zoom-select. Coarse pointer: the body long-press starts the swap.
    if (useHandle) return;
    if (dndListeners && dndListeners.onPointerDown) dndListeners.onPointerDown(e); // hand off to dnd-kit
  };
  // Right-click on the video → open the ••• menu (ZoomableContainer only calls this when NOT zoomed;
  // zoomed → it resets the zoom). Suppress the browser menu; bump the signal ActionsMenu watches.
  const handleContextMenu = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setMenuSignal((n) => n + 1);
  };

  return (
    <div
      ref={setDndRef}
      className={`mcpb-cell${zoomed ? ' mcpb-cell--zoomed' : ''}${dimmed ? ' mcpb-cell--dimmed' : ''}${flash ? ' mcpb-cell--flash' : ''}${selected ? ' mcpb-cell--selected' : ''}${hovered ? ' mcpb-cell--hover-peer' : ''}${actionsShown ? ' mcpb-cell--actions-shown' : ''}${dragging ? ' mcpb-cell--dragging' : ''}${dragTarget ? ' mcpb-cell--drag-target' : ''}`}
      style={{ '--cell-cam-color': 'var(--mcpb-color-accent)' }}
      data-mcpb-slot={slotIndex}
      // For browser journeys (ADR-209): which surface plays this cell and whether a decoded frame
      // has been drawn, since a <video> stays empty on the WebCodecs path and pixels alone can't tell.
      data-mcpb-camera={camera ? (camera.bCamera || camera.id || '') : ''}
      data-mcpb-media={mediaSurface ? 'webcodecs' : (videoUrl ? 'video' : 'none')}
      data-mcpb-painted={mediaSurface ? String(!!wcReady) : undefined}
      onPointerUp={handleCellPointerUp}
      onPointerDown={handlePointerDown}
      {...(!useHandle ? (dndAttributes || {}) : {})}
    >
      {/* Media surface: a WebCodecs <canvas> (dragon streaming playback) when the parent
          injects one, otherwise the native <video> (local NVR + cloud-legacy). The chrome
          below is identical either way; buffering/error come from props in the canvas case
          (the <video> onError/onLoadedData below only fire for the native element).
          Wrapped in the shared ZoomableContainer (same digital pinch/pan zoom Live uses — pinch
          1–6× / drag-select on desktop, drag-to-pan, tap/right-click to reset). The video STAYS in
          its cell; only its content scales. Chrome (name/timestamp/•••/overlays) renders OUTSIDE the
          zoom below so labels never scale. Disabled with no clip to zoom (a gap tile). */}
      <ZoomableContainer
        isEview={isEview}
        disabled={!videoUrl && !mediaSurface}
        className="mcpb-cell__zoom"
        style={{ position: 'absolute', inset: 0 }}
        onDoubleClick={handleCellDoubleClick}
        onDoubleClickWhenZoomed
        onZoomChange={(z) => { zoomApiRef.current = z; }}
        onContextMenu={handleContextMenu}
      >
        {mediaSurface || (
          /* eslint-disable-next-line jsx-a11y/media-has-caption */
          <video
            ref={videoRef}
            className="mcpb-cell__video"
            src={videoUrl || undefined}
            playsInline
            muted
            onError={() => { if (videoUrl && onMediaError) onMediaError(); }}
            onLoadedData={() => setFirstFrame(true)}
            onPlaying={() => setFirstFrame(true)}
          />
        )}
      </ZoomableContainer>

      {/* Grip handle (fine pointer / desktop mouse only) — the hover-revealed pull-tab that owns the
          drag-to-swap, so a body click-drag stays free for the digital zoom-select. Mirrors Live's
          .live-tile-drag-handle exactly (same grip glyph). Touch keeps the plain long-press body-drag and
          renders nothing over the video. The handle carries dnd-kit's listeners/attributes directly. */}
      {useHandle && (
        <button
          type="button"
          className="mcpb-cell__drag-handle"
          aria-label="Move camera — drag to swap"
          title="Drag to swap"
          {...(dndAttributes || {})}
          {...(dndListeners || {})}
          onClick={(e) => e.stopPropagation()}
          onDoubleClick={(e) => e.stopPropagation()}
        >
          <svg width="22" height="7" viewBox="0 0 22 7" fill="currentColor" aria-hidden="true"><rect x="4" y="1.5" width="14" height="1.4" rx="0.7" /><rect x="4" y="4.1" width="14" height="1.4" rx="0.7" /></svg>
        </button>
      )}

      {/* No-video base = Live's CameraPlaceholder (grey card + centered DIVIDIA logo + recessed inset
          shadows) — REUSED so playback cells match Live's no-signal tiles exactly (David 2026-08-04),
          replacing the old black + dtplaceholder.gif poster. Shown until the first frame decodes:
          native-video waits on firstFrame, WebCodecs waits on wcReady (hasEverPainted from SlotCell).
          Also covers the DnD stale-frame issue: swapping a WebCodecs cell resets wcReady → placeholder
          covers the old canvas pixels until the new camera's frame decodes. */}
      {(mediaSurface ? !wcReady : (!firstFrame && !(gap && hasLastFrame))) && (
        livePreview
          ? <img src={livePreview} alt="" className="mcpb-cell__live-preview" />
          : <CameraPlaceholder />
      )}

      {/* Frozen last frame (task 3, David 2026-08-05): when a cam that WAS playing drops into a gap, freeze
          its last decoded frame GREYED instead of the DIVIDIA placeholder. Always mounted (so the bitmap
          survives the play→gap transition) and revealed only in a gap once a frame has been captured;
          otherwise the placeholder + dark scrim below is the fallback. */}
      {!mediaSurface && (
        <canvas
          ref={lastFrameRef}
          className={`mcpb-cell__last-frame${gap && hasLastFrame ? ' mcpb-cell__last-frame--show' : ''}`}
          aria-hidden="true"
        />
      )}

      {/* Per-track gap (2a): this cam has no recorded clip at the current master offset. When we have a
          frozen last frame it's shown greyed (above) as the cue; otherwise the DARKENING of the tile IS
          the "no video available" cue over the CameraPlaceholder — no text (David 2026-08-04). Distinct
          from `loading` (spinner) below. */}
      {gap && !hasLastFrame && <div className="mcpb-cell__gap" aria-hidden="true" />}

      {/* Loading / buffering spinner — Live-parity: a scrim-less spinner (like Live's first-frame spinner)
          while events are still loading (`loading`), a clip is decoding its first frame (`loadingFrame`),
          or a clip-bearing tile is rebuffering / cloud-caching (`buffering`). `bufferingLabel` surfaces
          cloud cache progress ("Caching 45%"). No "Loading…" text (David 2026-08-04). */}
      {(loading || buffering || loadingFrame || recovering) && !failed && !loadError && !gap && (
        <div className="mcpb-cell__buffering" aria-label="Loading" aria-live="polite">
          <span className="mcpb-cell__spinner" />
          {bufferingLabel && <span className="mcpb-cell__buffering-label">{bufferingLabel}</span>}
        </div>
      )}

      {/* Media/load-error overlay (v1.5 + cloud): the clip failed to load/decode (mediaError) OR the cloud
          cache pull failed (loadError — e.g. "HTTP 401", "Timed out", surfaced instead of an endless
          spinner, mirroring Java downloadToFile). Identity (cam-name) stays readable above it so the user
          knows WHICH cam failed, and the ••• stays usable to swap the bad cam. */}
      {(failed || loadError) && (
        <div className="mcpb-cell__error" role="alert">
          <svg className="mcpb-cell__error-icon" viewBox="0 0 24 24" aria-hidden="true"><path d={ERROR_ICON_PATH} /></svg>
          <span>{loadError ? `Couldn’t load clip (${loadError})` : 'Can’t play this clip'}</span>
        </div>
      )}

      {/* Cog "Camera Info overlay" (David 2026-06-21): on-video VIDEO/STREAM diagnostics in the free
          TOP-LEFT corner (••• cluster is top-right; name/timestamp are bottom) — the desktop DstreamView
          "Toggle Info" (Res/FPS/Bitrate) equivalent. Lines are pre-formatted by the parent (mcpbVideoInfo);
          passive (aria-hidden — a decorative diagnostic). Sized to the cell (cqi) like the v1.5 overlays. */}
      {streamInfo && streamInfo.length > 0 && (
        <div className="mcpb-cell__info" aria-hidden="true">
          {streamInfo.map((line, i) => <span key={i}>{line}</span>)}
        </div>
      )}

      {/* Top-right cluster (#16): the surfaced quick-action row (revealed on hover/tap, or pinned by the
          cog "always-show" toggle; Archive/Export stay visible + filled while active) + the ••• menu.
          Empty-slot placeholders (fEnable:false) get NO actions/••• — clean empty tile (David). */}
      {camera.fEnable !== false && (
      <div className="mcpb-cell__chrome-tr">
        {quickActions.length > 0 && (
          <div className="mcpb-cell__quick-actions">
            {quickActions.map((qa) => (
              <button
                key={qa.action}
                type="button"
                className={`mcpb-cell__quick-action-btn${qa.active && qa.action === 'archive' ? ' mcpb-cell__quick-action-btn--on-archive' : ''}${qa.active && qa.action === 'export' ? ' mcpb-cell__quick-action-btn--on-export' : ''}`}
                title={qa.title}
                aria-label={qa.label}
                disabled={qa.disabled}
                onClick={() => onQuickAction && onQuickAction(qa.action)}
              >
                <svg viewBox="0 0 24 24" aria-hidden="true"><path d={qa.path} /></svg>
              </button>
            ))}
          </div>
        )}
        {menuItems.length > 0 && (
          <ActionsMenu items={menuItems} header={camLabel || `${camera.bCamera} - ${camera.sName}`} triggerIcon={<FaEllipsisH />} triggerAriaLabel={`Actions for ${camera.sName}`} align="right" openSignal={menuSignal} />
        )}
      </div>
      )}

      {/* Standard overlay set — cam name lower-left (also the change-camera button), this cell's OWN
          playback timestamp lower-right. One set, every cell/size/orientation (David 2026-06-20).
          Empty-slot placeholders (fEnable:false) render NO caption — the tile stays clean (David). */}
      {camera.fEnable !== false && (
        <button
          type="button"
          className="mcpb-cell__cam-name live-overlay__text"
          aria-label={`Change camera: ${camera.sName}`}
          title="Change camera"
          onClick={onChangeCamera}
        >
          {camLabel || `${camera.bCamera} - ${camera.sName}`}
        </button>
      )}
      {camera.fEnable !== false && infoText && !gap && <div className="mcpb-cell__timestamp live-overlay__time">{infoText}</div>}

      {/* Single-view focus (2d): double-tap the frame to fill the grid with this cam (conf>1), or use the
          "View single" item in the ••• menu — the dedicated ⤢ button was dropped (David 2026-06-20). */}

      {transportOverlay}
    </div>
  );
};

export default Cell;
